import { useState } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { useSteerData } from '@/hooks/useCommandCenter';
import { postFleetResume } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { HaltModal } from './HaltModal';
import { Sliders, AlertTriangle, Play, StopCircle } from 'lucide-react';

export function SteerView() {
  const { data, loading, refresh } = useSteerData();
  const [haltModalOpen, setHaltModalOpen] = useState(false);
  const [resuming, setResuming] = useState(false);

  const claw = data?.claw;
  const governance = data?.governance;
  const haltActive = claw?.fleetHaltActive ?? false;
  const steeringActive = claw?.contextSteering?.active ?? false;
  const alerts = governance?.alerting?.recentAlerts ?? [];

  const handleResume = async () => {
    setResuming(true);
    await postFleetResume();
    await refresh();
    setResuming(false);
  };

  const handleHaltComplete = () => {
    setHaltModalOpen(false);
    refresh();
  };

  if (loading) {
    return <div className="text-[#A8A29E] text-sm">Loading steer data...</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Steer Console</h2>

      {/* Badges */}
      <div className="flex gap-3">
        <Badge variant="outline" style={haltActive ? { color: GovernanceColors.critical, borderColor: GovernanceColors.critical } : { color: GovernanceColors.healthy, borderColor: GovernanceColors.healthy }}>
          Fleet {haltActive ? 'HALTED' : 'Active'}
        </Badge>
        <Badge variant="outline" style={steeringActive ? { color: GovernanceColors.streaming, borderColor: GovernanceColors.streaming } : { color: '#A8A29E', borderColor: '#4A443D' }}>
          Steering {steeringActive ? 'ON' : 'OFF'}
        </Badge>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sliders className="w-4 h-4" style={{ color: GovernanceColors.primary }} />
              <span className="text-sm font-medium">Context Steering</span>
            </div>
            <p className="text-xs text-[#A8A29E]">
              {steeringActive
                ? 'Context steering is active. Governance payloads are being injected per registered policies.'
                : 'Context steering is inactive.'}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[#292524] border-[#3A352F]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" style={{ color: haltActive ? GovernanceColors.critical : GovernanceColors.healthy }} />
              <span className="text-sm font-medium">Fleet Status</span>
            </div>
            <p className="text-xs text-[#A8A29E]">
              {haltActive
                ? 'Fleet is HALTED. All agent operations are suspended. Use Resume to restore operations.'
                : `Fleet is operational. ${claw?.registeredAgents ?? 0} agents registered.`}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Governance Actions */}
      <Card className="bg-[#292524] border-[#3A352F]">
        <CardContent className="p-4 space-y-4">
          <h3 className="text-sm font-medium">Governance Actions</h3>
          <div className="flex gap-3 flex-wrap">
            <Button
              variant="outline"
              className="border-[#4A443D] text-[#A8A29E] hover:text-[#F5F0E8] hover:bg-[#3D3833]"
              disabled
            >
              <Play className="w-4 h-4 mr-2" />
              Manual Steer (Tier 4+)
            </Button>
            <Button
              variant="outline"
              className="hover:bg-[#34D399]/10"
              style={{ borderColor: GovernanceColors.healthy, color: GovernanceColors.healthy }}
              onClick={handleResume}
              disabled={!haltActive || resuming}
            >
              {resuming ? 'Resuming...' : 'Resume Fleet'}
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-6"
              onClick={() => setHaltModalOpen(true)}
            >
              <StopCircle className="w-4 h-4 mr-2" />
              HALT ALL AGENTS
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Alerts */}
      <Card className="bg-[#292524] border-[#3A352F]">
        <CardContent className="p-4">
          <h3 className="text-sm font-medium mb-3">Recent Alerts</h3>
          {alerts.length === 0 ? (
            <p className="text-xs text-[#8F857B]">No recent alerts.</p>
          ) : (
            <ul className="space-y-2">
              {alerts.slice(0, 10).map((alert: any, i: number) => (
                <li key={i} className="text-xs text-[#A8A29E] flex items-start gap-2">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" style={{ color: GovernanceColors.warning }} />
                  <span>{alert.message || JSON.stringify(alert)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Halt Modal */}
      <HaltModal
        open={haltModalOpen}
        onClose={() => setHaltModalOpen(false)}
        onComplete={handleHaltComplete}
      />
    </div>
  );
}
